import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { DashboardShell } from "@/components/DashboardShell";
import { SUPER_ADMIN_NAV } from "@/lib/nav";
import {
  fetchActivityLogs,
  activityLabel,
  activityTone,
  activityTypeOptions,
  type ActivityRow,
} from "@/lib/activity";
import { ROLE_LABELS, type AppRole } from "@/lib/auth";

const TONE_CLASS: Record<string, string> = {
  good: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  bad: "bg-destructive/15 text-destructive",
  warn: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  neutral: "bg-secondary text-muted-foreground",
};

function when(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function summarise(row: ActivityRow) {
  const bits: string[] = [];
  if (row.entity_type) bits.push(row.entity_type.replace(/_/g, " "));
  if (row.service_type) bits.push(row.service_type);
  const meta = row.metadata ?? {};
  for (const key of ["title", "listingTitle", "plan", "kind", "decision", "reason"]) {
    const v = meta[key];
    if (typeof v === "string" && v.trim()) bits.push(`${key}: ${v}`);
  }
  return bits.join(" · ") || "—";
}

function ActivityLogsPage() {
  const [user, setUser] = useState("");
  const [role, setRole] = useState<AppRole | "all">("all");
  const [type, setType] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const filters = { user, role, type, from, to };
  const { data, isLoading, error } = useQuery({
    queryKey: ["super-admin", "activity-logs", filters],
    queryFn: () => fetchActivityLogs(filters),
  });

  const rows = data ?? [];
  const types = useMemo(() => activityTypeOptions(rows), [rows]);
  const active = user || role !== "all" || type !== "all" || from || to;

  const field =
    "w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/25";

  return (
    <DashboardShell title="Activity Logs" role="Super Admin" items={SUPER_ADMIN_NAV}>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Read-only, append-only record of platform activity. Entries cannot be edited or deleted.
        </p>

        <div className="grid gap-3 rounded-xl border border-border bg-card p-4 sm:grid-cols-2 lg:grid-cols-5">
          <label className="space-y-1.5 text-xs font-medium text-muted-foreground">
            User
            <input
              className={field}
              value={user}
              onChange={(e) => setUser(e.target.value)}
              placeholder="Name, email or ID"
            />
          </label>
          <label className="space-y-1.5 text-xs font-medium text-muted-foreground">
            Role
            <select
              className={field}
              value={role}
              onChange={(e) => setRole(e.target.value as AppRole | "all")}
            >
              <option value="all">All roles</option>
              {(Object.keys(ROLE_LABELS) as AppRole[]).map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1.5 text-xs font-medium text-muted-foreground">
            Activity type
            <select className={field} value={type} onChange={(e) => setType(e.target.value)}>
              <option value="all">All activity</option>
              {types.map((t) => (
                <option key={t} value={t}>
                  {activityLabel(t)}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1.5 text-xs font-medium text-muted-foreground">
            From
            <input
              type="date"
              className={field}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </label>
          <label className="space-y-1.5 text-xs font-medium text-muted-foreground">
            To
            <input
              type="date"
              className={field}
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </label>
          {active && (
            <button
              type="button"
              onClick={() => {
                setUser("");
                setRole("all");
                setType("all");
                setFrom("");
                setTo("");
              }}
              className="justify-self-start rounded-lg border border-border px-3 py-2 text-xs font-semibold hover:bg-secondary"
            >
              Clear filters
            </button>
          )}
        </div>

        <div className="rounded-xl border border-border bg-card">
          <div className="flex items-center justify-between border-b border-border px-4 py-3 text-sm">
            <span className="font-semibold">Activity</span>
            <span className="text-xs text-muted-foreground">{rows.length} entries</span>
          </div>

          {isLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Loading activity…</p>
          ) : error ? (
            <p className="p-6 text-sm text-destructive">{(error as Error).message}</p>
          ) : rows.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">No activity matches these filters.</p>
          ) : (
            <>
              {/* Mobile */}
              <ul className="divide-y divide-border md:hidden">
                {rows.map((r) => (
                  <li key={r.id} className="space-y-1.5 p-4">
                    <div className="flex items-center justify-between gap-2">
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-semibold ${TONE_CLASS[activityTone(r.action)]}`}
                      >
                        {activityLabel(r.action)}
                      </span>
                      <span className="text-xs text-muted-foreground">{when(r.created_at)}</span>
                    </div>
                    <p className="text-sm font-medium">
                      {r.actorName ?? r.actorEmail ?? "Unknown user"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {r.actor_role ? ROLE_LABELS[r.actor_role] : "—"} · {summarise(r)}
                    </p>
                  </li>
                ))}
              </ul>

              {/* Desktop */}
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full text-left text-sm">
                  <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                    <tr className="border-b border-border">
                      <th className="px-4 py-2.5 font-medium">When</th>
                      <th className="px-4 py-2.5 font-medium">User</th>
                      <th className="px-4 py-2.5 font-medium">Role</th>
                      <th className="px-4 py-2.5 font-medium">Activity</th>
                      <th className="px-4 py-2.5 font-medium">Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {rows.map((r) => (
                      <tr key={r.id} className="align-top">
                        <td className="whitespace-nowrap px-4 py-3 text-xs text-muted-foreground">
                          {when(r.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium">{r.actorName ?? "Unknown user"}</div>
                          <div className="text-xs text-muted-foreground">
                            {r.actorEmail ?? r.actor_id ?? "—"}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs">
                          {r.actor_role ? ROLE_LABELS[r.actor_role] : "—"}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`rounded-full px-2.5 py-1 text-xs font-semibold ${TONE_CLASS[activityTone(r.action)]}`}
                          >
                            {activityLabel(r.action)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">{summarise(r)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}

export const Route = createFileRoute("/_authenticated/super-admin/activity-logs")({
  head: () => ({
    meta: [
      { title: "Activity Logs · Landoo Super Admin" },
      {
        name: "description",
        content: "Read-only platform activity log with user, role, date and type filters.",
      },
      { property: "og:title", content: "Activity Logs · Landoo Super Admin" },
      {
        property: "og:description",
        content: "Read-only platform activity log for Landoo super admins.",
      },
    ],
  }),
  component: ActivityLogsPage,
});
